import React from 'react';
import {ColorInputWithoutInstance} from 'tinycolor2';

import * as wast from '@packages/systems/wfdl-ast';

import {
  ExpressionPath,
  Expressions,
  TypeImplementation,
  withSystem,
  Plugins,
  Result,
} from '@packages/systems/wfdl';
import {TRecord} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  DEFAULT_BREAKPOINT_IDS,
  LARGER_BREAKPOINT_IDS,
} from '@packages/systems/style/breakpoint-ids';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  FRAME_BACKGROUND_COLOR_CHANGED,
  FRAME_BACKGROUND_COLOR_OVERRIDE_RESET,
} from '@packages/systems/designer/actions';
import {
  colorToHSL,
  HSLA,
  parseColor,
} from '@packages/systems/core/utils/ColorUtils';
import {
  DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {
  getFrameById,
  getBaseVariantFrame,
  getVariantInstanceElementFromFrame,
  getVariantAssociatedWithFrame,
  getOverrides,
  selectGetBaseVariantId,
  selectSelectedFrameElementId,
} from '@packages/systems/component-canvas/designer';

import type {BindableColorValue} from '@packages/systems/component-canvas/types';
import {selectComponents} from '@packages/utilities/platform-selectors';
import {HStack, Tag} from '@packages/systems/spring';
import {FrameFieldLabel, findInheritedFrom} from './FrameFieldLabel';
import {
  CustomRawPrimitive,
  VariableReference,
} from '@packages/systems/core/utils/StyleVariables';
import {emotionStyled} from '@packages/utilities/styled';
import {ColorPickerInput} from '@packages/systems/plugins/Color';
import {selectBodyBackgroundColorValue} from './utils/selectBodyBackgroundColor';
import {getBreakpointID} from '@packages/systems/style/types';

import {getObject, Json} from '@packages/utilities/json';
import {isHSLA, colorParseValue} from '@packages/systems/plugins-utils/Color';
import {
  colorExpressionValueToAnyVariableValue,
  FrameBackgroundColorBindingMenu,
} from './FrameBackgroundColorBindingMenu';

// type guard for ColorInputWithoutInstance
function isColorInputWithoutInstance(
  value: unknown
): value is ColorInputWithoutInstance {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    (typeof value === 'object' && value !== null)
  );
}

const DEFAULT_FRAME_BACKGROUND_COLOR_LITERAL: BindableColorValue = {
  type: 'body-background-color',
};

const bindableColorTypeName = qualify(
  Plugins.name('Frame'),
  typeName('BindableColor')
);

const TBindableColor = wast.TTypeConstructor(bindableColorTypeName);

const breakpointIds = [...DEFAULT_BREAKPOINT_IDS, ...LARGER_BREAKPOINT_IDS];

const createFrameBackgroundColorPath = (mediaQuery: string): ExpressionPath => {
  return [
    {in: 'Record', at: mediaQuery},
    {in: 'Record', at: 'color'},
  ];
};

const selectMediaQuery = (state: DesignerStores) =>
  state.ExternalStore.mediaQuery;

const isVariableRef = (json: Json) => {
  const obj = getObject(json);
  if (!obj) return false;

  return obj.type === 'ref';
};

const isCustomRawPrimitive = (json: Json) => {
  const obj = getObject(json);
  if (!obj) return false;

  return obj.type === 'raw';
};

const isBodyBackgroundColor = (json: Json) => {
  const obj = getObject(json);
  if (!obj) return false;

  return obj.type === 'body-background-color';
};

export const BindableColorType = TypeImplementation.Custom({
  render: () => null,
  parseValue: (json) => {
    if (isHSLA(json)) return colorParseValue(json);

    if (isVariableRef(json)) return Result.Ok(json);

    if (isCustomRawPrimitive(json)) return Result.Ok(json);

    if (isBodyBackgroundColor(json)) return Result.Ok(json);

    return Result.Error('Invalid frame background color');
  },
});

const FrameBackgroundColorDataType = TRecord(
  Object.fromEntries(
    breakpointIds.map((breakpoint) => [
      breakpoint,
      TRecord({
        color: withSystem(
          {
            label: 'Color',
            labelVariant: 'hidden',
            defaultValue: DEFAULT_FRAME_BACKGROUND_COLOR_LITERAL,
          },
          TBindableColor
        ),
      }),
    ])
  )
);

const DotPositioner = emotionStyled('div')({
  position: 'absolute',
  top: '-$2',
  left: '-$2',
});

const WrapperView = emotionStyled('div')(
  {
    flex: 4,
    position: 'relative',
    width: '100%',
    [`${DotPositioner}`]: {
      opacity: 0,
    },
    '&:hover, &:focus, &:focus-within': {
      [`${DotPositioner}`]: {
        opacity: 1,
      },
    },
  },
  {
    variants: {
      isBindingMenuOpen: {
        true: {
          [`${DotPositioner}`]: {
            opacity: 1,
          },
        },
      },
    },
  }
);

function FrameBackgroundColorRender({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) {
  const mediaQuery = useDesignerSelector(selectMediaQuery);
  const selectedFrameElementId = useDesignerSelector(
    selectSelectedFrameElementId
  );
  const getBaseVariantId = useDesignerSelector(selectGetBaseVariantId);
  const componentMap = useDesignerSelector(selectComponents);

  const currentFrame = React.useMemo(() => {
    if (!selectedFrameElementId) return null;
    return getFrameById(selectedFrameElementId, componentMap);
  }, [selectedFrameElementId, componentMap]);

  const instanceElement = React.useMemo(() => {
    if (!currentFrame) return null;
    return getVariantInstanceElementFromFrame(currentFrame, componentMap);
  }, [currentFrame, componentMap]);

  const [isBindingMenuOpen, setIsBindingMenuOpen] = React.useState(false);

  const breakpointId = getBreakpointID(mediaQuery) ?? 'main';
  const bodyBackgroundColorValue = useDesignerSelector(
    selectBodyBackgroundColorValue(breakpointId)
  );

  if (!selectedFrameElementId || !currentFrame || !instanceElement) return null;

  const baseFrame = getBaseVariantFrame(componentMap, getBaseVariantId);
  if (!baseFrame) return null;

  const variant = getVariantAssociatedWithFrame(
    selectedFrameElementId,
    componentMap
  );
  if (!variant) return null;

  const frameOverrides = getOverrides(currentFrame);
  const hasColorOverride = Boolean(frameOverrides[mediaQuery]?.color);

  const baseVariant = getVariantAssociatedWithFrame(baseFrame.id, componentMap);
  const baseVariantName = baseVariant?.displayName ?? variant.displayName;
  const inheritedFrom = findInheritedFrom(
    currentFrame,
    variant.displayName,
    baseFrame,
    baseVariantName,
    mediaQuery
  )('color') || (
    <Tag variant="solid" colorVariant="pink" density="compact">
      Body (All Pages)
    </Tag>
  );
  const colorPath = createFrameBackgroundColorPath(mediaQuery);
  const colorExpr = ContextualExpressions.getIn(expr, colorPath) ?? expr;

  // Check if base frame color is overridden from default
  const currentColorValue = Expressions.getLiteral(stripAll(colorExpr))?.value;

  const handleColorReset = () => {
    dispatch({
      type: FRAME_BACKGROUND_COLOR_OVERRIDE_RESET,
      payload: {mediaQuery},
      key: colorPath,
    });
  };

  const colorValueForBindMenu = colorExpressionValueToAnyVariableValue(
    currentColorValue as BindableColorValue,
    bodyBackgroundColorValue
  );

  const handleBindingChange = (
    value: HSLA | VariableReference | CustomRawPrimitive
  ) => {
    dispatch({
      type: FRAME_BACKGROUND_COLOR_CHANGED,
      key: colorPath,
      payload: {
        value,
        mediaQuery,
      },
    });
  };

  const handleColorStringChange = (value: string) => {
    // Validate color before dispatching
    if (!value || !isColorInputWithoutInstance(value)) return;
    const isValidColor = Boolean(parseColor(value));
    if (!isValidColor) return;

    dispatch({
      type: FRAME_BACKGROUND_COLOR_CHANGED,
      payload: {
        value: colorToHSL(value),
        mediaQuery,
      },
      key: colorPath,
    });
  };

  const bindMenu = (
    <FrameBackgroundColorBindingMenu
      colorValueForBindMenu={colorValueForBindMenu}
      onBindingChange={handleBindingChange}
      isBindingMenuOpen={isBindingMenuOpen}
      setIsBindingMenuOpen={setIsBindingMenuOpen}
    />
  );

  return (
    <HStack spacing={1} align="center">
      <FrameFieldLabel
        label="Color"
        inheritedFrom={inheritedFrom}
        automationId="Frame-colorLabel"
        onReset={hasColorOverride ? handleColorReset : undefined}
      />
      <WrapperView isBindingMenuOpen={isBindingMenuOpen}>
        {colorValueForBindMenu.type !== 'color' ? (
          bindMenu
        ) : (
          <>
            <ColorPickerInput
              value={colorValueForBindMenu.value}
              onChange={handleColorStringChange}
              dispatch={dispatch}
              data-automation-id="frame-background-color-picker-input"
            />
            <DotPositioner>{bindMenu}</DotPositioner>
          </>
        )}
      </WrapperView>
    </HStack>
  );
}

export const FrameBackgroundColorType = TypeImplementation.Alias({
  type: FrameBackgroundColorDataType,
  render: FrameBackgroundColorRender,
});
